import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { MessageService } from 'primeng/api';
import { Observable, Subject } from 'rxjs';
import { takeUntil, first } from 'rxjs/operators';

import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { User } from 'app/auth/models';
import { AuthenticationService } from 'app/auth/service';
import { ConfigService } from 'app/app-config.service';
import { DCarbonizeAppConfig } from 'app/@types/shared/app-config';
import { ToastService } from '@services/common/toast.service';
import { Select } from '@ngxs/store';
import { OrganizationState } from '@state/organization.state';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { FreetrialExpirationComponent } from 'app/main/apps/components/freetrial-expiration/freetrial-expiration.component';
import { TermsComponent } from 'app/main/apps/components/terms/terms.component';
import { error } from 'console';
import { HttpClient } from '@angular/common/http';
import { UrlService } from '@services/datasources/url.service';

interface LoginFormGroup {
    email: FormControl<string>;
    password: FormControl<string>;
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    providers: [MessageService, DialogService],
})
export class LoginComponent implements OnInit, OnDestroy {
    //  Public
    public loginForm!: FormGroup<LoginFormGroup>;
    public loading: boolean = false;
    public returnUrl!: string;
    modal: DynamicDialogRef | undefined;
    public registerParams: { [key: string]: string } | undefined;
    // Private
    private unsubscribeAll: Subject<void> = new Subject<void>();
    private termsRef: DynamicDialogRef | undefined;
    private ROUTE_MEASURE = '/app/measure';

    config: DCarbonizeAppConfig;

    //Observer
    @Select(OrganizationState.getOrganizationPricingPlan)
    organizationPricingPlan$: Observable<OrganizationPricingPlanDto>;
    @Select(UserState.getCurrentUserClaims) userclaim$: Observable<UserClaims>;
    @Select(UserState.getCurrentUser) user$: Observable<User>;

    /**
     * Constructor
     *
     * @param {AuthenticationService} _authenticationService
     * @param {AppRoutingService} _appRoutingService
     * @param {UntypedFormBuilder} _formBuilder
     * @param {MessageService} _messageService
     * @param {ActivatedRoute} _route
     * @param {Router} _router
     * @param {CustomTranslateService} _translateService
     */
    constructor(
        public _authenticationService: AuthenticationService,
        private _appRoutingService: AppRoutingService,
        private _formBuilder: UntypedFormBuilder,
        private _messageService: MessageService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _translateService: CustomTranslateService,
        private configService: ConfigService,
        private _toastService: ToastService,
        private _dialogService: DialogService,
        private route: ActivatedRoute,
        private http: HttpClient,
        private urlService: UrlService,
    ) {
        this.config = this.configService.getConfig;
        this._authenticationService.isLoggedIn().subscribe((isloggedIn) => {
            if (isloggedIn) {
                this.user$.subscribe((user) => {
                    if (user.has_accepted_terms === '0' && !this.termsRef) {
                        this.termsRef = this._dialogService.open(TermsComponent, {
                            width: '521px',
                            height: '536px',
                            closable: true,
                            styleClass: 'terms-modal',
                        });
                        this.termsRef.onClose.subscribe((termsAccepted: boolean) => {
                            if (termsAccepted) this._router.navigate([this.ROUTE_MEASURE]);
                            else this._authenticationService.logout(true).pipe(first()).subscribe();
                        });
                    } else {
                        this._router.navigate([this.ROUTE_MEASURE]);
                    }
                });
            }
        });
    }

    // convenience getter for easy access to form fields
    public get f() {
        return this.loginForm.controls;
    }

    public onSubmit() {
        // stop here if form is invalid
        if (this.loginForm.invalid) {
            if (this.loginForm.controls.email.value.length === 0) {
                this.loginForm.controls.email.markAsDirty();
            }
            if (this.loginForm.controls.password.value.length === 0) {
                this.loginForm.controls.password.markAsDirty();
            }

            return;
        }
        this._messageService.clear();
        // Login
        this.loading = true;
        this._authenticationService
            .login(this.f.email.value, this.f.password.value)
            .pipe(first())
            .subscribe({
                next: (data: User | string) => {
                    if (data instanceof User) {
                        this.http
                            .get<UserClaims>(this.urlService.getUserClaimsUrl(undefined), { observe: 'response' })
                            .subscribe({
                                next: (userClaim) => {
                                    if (data.locale !== undefined) {
                                        this._translateService.use(data.locale);
                                    }
                                    if (data.has_accepted_terms === '0' && !this.termsRef) {
                                        this.termsRef = this._dialogService.open(TermsComponent, {
                                            width: '521px',
                                            height: '536px',
                                            closable: true,
                                            styleClass: 'terms-modal',
                                        });
                                        this.termsRef.onClose.subscribe((termsAccepted: boolean) => {
                                            if (termsAccepted) this.navigateToNextScreen(data);
                                            else this._authenticationService.logout(true).pipe(first()).subscribe();
                                        });
                                    } else {
                                        this.navigateToNextScreen(data);
                                    }
                                },
                            });
                    } else {
                        this.navigateToNextScreen(data);
                    }
                },
                // eslint-disable-next-line @typescript-eslint/typedef
                error: (error) => {
                    this.loading = false;
                    if (error === 'InitFailed') {
                        this._translateService.get('authentication.login.errors.initFailed').subscribe((msg) => {
                            this._toastService.showError({ details: msg as string, life: 5000 });
                        });
                        // use subscribe so it actually does the thing
                        this._authenticationService.logout(false).pipe(first()).subscribe();
                        return;
                    }
                    if (error.code === 'UserNotConfirmedException') {
                        this._authenticationService.beginValidation(this.f.email.value, this.f.password.value);
                        this._router.navigateByUrl('/auth/verification');
                        return;
                    }
                    this._messageService.clear();
                    this._messageService.add({ severity: 'error', detail: this.displayErrorMessage(error) });
                    this._authenticationService.logout(false).pipe(first()).subscribe();
                },
            });
    }

    private displayErrorMessage(error): string {
        const key = `authentication.login.${error.code}`;
        if (this._translateService.keyExists(key)) {
            return this._translateService.instant(key);
        } else {
            return error.message;
        }
    }

    public isFormInvalid(): boolean {
        return (
            this.loginForm.pristine === true ||
            (this.loginForm.pristine === false && (this.f.email.invalid || this.f.password.invalid))
        );
    }

    private navigateToNextScreen(data: string | User) {
        if (data === 'newPasswordRequired') {
            this._router.navigate(['/auth/new-password-required']);
        } else {
            this._appRoutingService.onSignedIn(this.returnUrl).catch();
        }
    }

    // Lifecycle Hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        Chathive.widget.hide();
        this.loginForm = this._formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required],
        });

        this._messageService.add({ severity: 'info', detail: this._route.snapshot.queryParams['msg'] });
        this.returnUrl = this._route.snapshot.queryParams['returnUrl'];
        // Check if the user is already logged in
        this._authenticationService
            .isLoggedIn()
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((isLoggedIn: boolean) => {
                if (isLoggedIn) {
                    const currentUser: User | null = this._authenticationService.currentUserValue;
                    // After Signin with federated login, the user locale in the token might not be the same as the login page
                    if (currentUser && currentUser.locale !== undefined) {
                        this._translateService.use(currentUser.locale);
                        this.navigateToNextScreen(currentUser);
                    } else this._appRoutingService.onSignedIn(this.returnUrl).catch();
                }
            });

        this.route.queryParams.subscribe((params) => {
            if (params['licenseExpired']) {
                this._dialogService
                    .open(FreetrialExpirationComponent, {
                        modal: true,
                        dismissableMask: true,
                        autoZIndex: true,
                        closable: true,
                        closeOnEscape: true,
                        showHeader: true,
                        transitionOptions: '300ms cubic-bezier(0, 0, 0.2, 1)',
                        styleClass: 'freetrial-dialog',
                    })
                    .onClose.subscribe(() => {
                        this.loading = false;
                        this._router.navigate(['/auth/login']);
                    });
            }

            if (params['subclientId']) {
                this.registerParams = { subclientId: params['subclientId'] };
            }
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        Chathive.widget.show();
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }
}
